import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import Draggable from 'react-draggable';
import '../style.scss';
import { TbArrowsMinimize, TbArrowsMaximize } from 'react-icons/tb';
import { MdClose } from 'react-icons/md';
import AssetHubOptions from './AssetHubOptions';
import AssetHubLegend from './AssetHubLegends';
import AssetHubInfo from './AssetHubInfo';
import { assetActions, AssetInfo } from 'actions/asset';
import { FiRefreshCw } from 'react-icons/fi';
import '../style.scss';
import type { AssetHubModalDetails } from './assetHubModalTypes';
import { getUnknownErrorMessage } from './assetHubModalTypes';
import type { InspectionSelection } from './assetHubInspectionSelection';
import useFetchInspectionRecord from 'customHooks/useFetchInspectionRecord';
import useFetchInspectionReports from 'customHooks/useFetchInspectionReports';

import type { AttributesModalData } from '../attributeResolvers';

import type { AssetAttributeModalDetails } from '../attributeDetailsTypes';

interface AssetHubProps {
  closeModal: (title: string) => void;
  data: AttributesModalData & { readonly id: number };
  setWasAssetHubRefreshedClicked: React.Dispatch<React.SetStateAction<boolean>>;
  assetHubRefreshToken: number;
  setAssetHubRefreshToken: React.Dispatch<React.SetStateAction<number>>;
  setWasAssetHubRefreshLoading: React.Dispatch<React.SetStateAction<boolean>>;
  wasAssetHubRefreshedClicked: boolean;
  wasAssetHubRefreshLoading: boolean;
  initialDetails?: AssetAttributeModalDetails;
  readonly zIndex?: number;
}
const FAKE_BRIDGES = [
  {
    name: 'Trent Bridge',
    src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Gaoliang_Bridge.JPG/1200px-Gaoliang_Bridge.JPG',
  },
  {
    name: 'Second Bridge',
    src: 'https://live.staticflickr.com/65535/36767106826_8f98d07e23_b.jpg',
  },
  {
    name: 'Third Bridge',
    src: 'https://i.pinimg.com/originals/a8/b0/b4/a8b0b4f3713a761c729540cb96c356cf.jpg',
  },
  {
    name: 'Trent Bridge',
    src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/Gaoliang_Bridge.JPG/1200px-Gaoliang_Bridge.JPG',
  },
  {
    name: 'Second Bridge',
    src: 'https://live.staticflickr.com/65535/36767106826_8f98d07e23_b.jpg',
  },
  {
    name: 'Third Bridge',
    src: 'https://i.pinimg.com/originals/a8/b0/b4/a8b0b4f3713a761c729540cb96c356cf.jpg',
  },
];

const AssetHub: React.FC<AssetHubProps> = ({
  closeModal,
  data,
  wasAssetHubRefreshedClicked,
  wasAssetHubRefreshLoading,
  setWasAssetHubRefreshLoading,
  setWasAssetHubRefreshedClicked,
  assetHubRefreshToken,
  setAssetHubRefreshToken,
  initialDetails,
  zIndex,
}) => {
  const [isModalMinimized, setModalMinimized] = React.useState<boolean>(false);
  const toggleModalState = () => setModalMinimized((prevState) => !prevState);
  const [modalDetails, setModalDetails] = React.useState<
    AssetHubModalDetails | undefined
  >(initialDetails);
  const [infoTrigger, setInfoTrigger] = React.useState<boolean>(false);
  const [spanInfoError, setSpanInfoError] = React.useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] =
    useState<InspectionSelection | null>(null);
  const selectedInspectionRef = useRef(selectedInspection);
  selectedInspectionRef.current = selectedInspection;
  const hasLoadedDetailsRef = useRef(Boolean(initialDetails));
  const assetId = data.id;
  const {
    inspectionRecords,
    inspectionsAvailable,
    isLoading: inspectionRecordsLoading,
    isError: inspectionRecordsError,
  } = useFetchInspectionRecord(assetId, assetHubRefreshToken);
  const {
    inspectionReports,
    reportsLoading,
    reportsError,
  } = useFetchInspectionReports(
    assetId,
    assetHubRefreshToken,
    inspectionRecords,
    inspectionRecordsLoading,
  );

  useEffect(() => {
    hasLoadedDetailsRef.current = Boolean(initialDetails);
  }, [assetId]);

  useEffect(() => {
    const refreshData = assetHubRefreshToken > 0;

    // Prefer details already loaded by Attributes; fetch only when missing or refreshing.
    if (!refreshData && initialDetails) {
      if (!selectedInspectionRef.current) {
        setModalDetails(initialDetails);
      }
      hasLoadedDetailsRef.current = true;
      setSpanInfoError(false);
      setWasAssetHubRefreshLoading(false);
      return;
    }

    if (!refreshData && hasLoadedDetailsRef.current) {
      setWasAssetHubRefreshLoading(false);
      return;
    }

    assetActions()
      .getAssetDetails(assetId, refreshData)
      .then((result: AssetInfo) => {
        hasLoadedDetailsRef.current = true;
        if (!selectedInspectionRef.current) {
          setModalDetails(result);
        }
        setSpanInfoError(false);
        setWasAssetHubRefreshLoading(false);
      })
      .catch((error: unknown) => {
        console.error('Asset Hub load failed:', getUnknownErrorMessage(error));
        if (!selectedInspectionRef.current) {
          setModalDetails(null);
        }
        setSpanInfoError(true);
        setWasAssetHubRefreshLoading(false);
      });
  }, [
    assetId,
    assetHubRefreshToken,
    infoTrigger,
    initialDetails,
    setWasAssetHubRefreshLoading,
  ]);

  return (
    <>
      <Modal
        ariaHideApp={false}
        isOpen={true}
        bodyOpenClassName={null}
        portalClassName="react-modal-container"
        overlayClassName="react-modal-overlay"
        className="react-modal-wrapper"
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
      >
        <Draggable
          enableUserSelectHack={false}
          handle=".modal-header"
          bounds="body"
        >
          <div
            className={`react-modal with-asset-hub top-left attributes-selected-item-modal${isModalMinimized ? ' minimized' : ''}`}
            style={zIndex != null ? { zIndex } : undefined}
          >
            <div className="modal-header">
              <div className="modal-title-container-asset-hub">
                <div className="modal-title">Asset Hub </div>&nbsp;
                <FiRefreshCw
                  className={
                    wasAssetHubRefreshedClicked && wasAssetHubRefreshLoading
                      ? 'modal-refresh-button  rotating'
                      : 'modal-refresh-button '
                  }
                  onClick={() => {
                    setWasAssetHubRefreshedClicked(true);
                    setAssetHubRefreshToken((token) => token + 1);
                    setWasAssetHubRefreshLoading(true);
                  }}
                />
              </div>
              <div className="modal-header-buttons">
                <div className="modal-control" onClick={toggleModalState}>
                  {isModalMinimized ? (
                    <TbArrowsMaximize />
                  ) : (
                    <TbArrowsMinimize />
                  )}
                </div>
                <div
                  className="modal-close"
                  onClick={() => closeModal('Attributes')}
                >
                  <MdClose />
                </div>
              </div>
            </div>
            <div className="modal-content">
              <div className="asset_hub--container">
                <AssetHubLegend
                  data={data}
                  modalDetails={modalDetails}
                  setModalDetails={setModalDetails}
                  infoTrigger={infoTrigger}
                  setInfoTrigger={setInfoTrigger}
                  setSpanInfoError={setSpanInfoError}
                  assetHubRefreshToken={assetHubRefreshToken}
                  selectedInspection={selectedInspection}
                  setSelectedInspection={setSelectedInspection}
                  inspectionRecords={inspectionRecords}
                  inspectionRecordsLoading={inspectionRecordsLoading}
                />
                <AssetHubInfo
                  modalDetails={modalDetails}
                  spanInfoError={spanInfoError}
                />
                <AssetHubOptions
                  id={assetId}
                  FAKE_BRIDGES={FAKE_BRIDGES}
                  assetHubRefreshToken={assetHubRefreshToken}
                  wasAssetHubRefreshLoading={wasAssetHubRefreshLoading}
                  selectedInspection={selectedInspection}
                  onInspectionSelect={setSelectedInspection}
                  inspectionRecords={inspectionRecords}
                  inspectionsAvailable={inspectionsAvailable}
                  inspectionRecordsLoading={inspectionRecordsLoading}
                  inspectionRecordsError={inspectionRecordsError}
                  inspectionReports={inspectionReports}
                  reportsLoading={reportsLoading}
                  reportsError={reportsError}
                />
              </div>
            </div>
          </div>
        </Draggable>
      </Modal>
    </>
  );
};

export default AssetHub;
